import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import { LocationOn, Phone, Email, Schedule, Send } from "@mui/icons-material";

import { motion } from "framer-motion";

import { useNotification } from "../../contexts/NotificationContext";
import { useRestaurant } from "../../contexts/RestaurantContext";
import api from "../../services/api";
import colors from "../../components/colors";
import BusinessHoursDisplay from "../../components/common/BusineesHours";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const fade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const ContactPage = () => {
  const { outlets } = useRestaurant();
  const { showSuccess, showError } = useNotification();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    inquiry_type: "general",
  });

  const inquiryTypes = [
    { value: "general", label: "General Inquiry" },
    { value: "reservation", label: "Table Reservation" },
    { value: "catering", label: "Catering Services" },
    { value: "complaint", label: "Complaint" },
    { value: "compliment", label: "Compliment" },
    { value: "feedback", label: "Feedback" },
    { value: "other", label: "Other" },
  ];
  const [countryNoLength, setCountryNoLength] = useState(0)

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  }

  const handleNumberChange = (field, e) => {
    let phoneNumber_Length = e.target.countryValue.format.split('.').length - 1;
    setCountryNoLength(phoneNumber_Length)
    setFormData({ ...formData, [field]: e.target.value });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      showError("Please fill required fields");
      return;
    }

    if (formData.phone !== "" && formData.phone.length !== countryNoLength) {
      showError("Please Enter Valid Phone Number")
      return;
    }

    if (formData.phone !== "") {
      formData.phone = "+" + formData.phone
    }

    try {
      setLoading(true);
      await api.post("/public/contact", formData);
      showSuccess("Message sent!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        inquiry_type: "general",
      });
    } catch (err) {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ⭐ HERO SECTION ANIMATED */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <Box
          sx={{
            mt: 6,
            width: "100%",
            py: { xs: 6, sm: 8, md: 10 },
            textAlign: "center",
            background:
              `linear-gradient(135deg,${colors.primary.main}  0%, ${colors.secondary.main} 50%, ${colors.primary.main} 100%)`,
            color: "white",
            mt: 10,
          }}
        >
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <Typography
              sx={{
                fontSize: {
                  xs: "2.4rem !important",
                  sm: "2.8rem !important",
                  md: "3.2rem !important",
                },
                fontWeight: "800 !important",
              }}
            >
              Contact Us
            </Typography>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
          >
            <Typography
              sx={{
                mt: 2,
                maxWidth: 650,
                mx: "auto",
                opacity: 0.9,
                fontSize: {
                  xs: "1rem !important",
                  sm: "1.15rem !important",
                  md: "1.25rem !important",
                },
                fontWeight: "500 !important",
              }}
            >
              We're here to assist you with reservations, feedback, inquiries,
              or anything you need.
            </Typography>
          </motion.div>
        </Box>
      </motion.div>

      <Container
        sx={{
          py: {
            xs: 4,
            md: 6,
            overflowX: "hidden !important",
            overflowY: "hidden !important",
          },
        }}
      >
        <Grid
          container
          sx={{
            overflowX: "hidden !important",
            overflowY: "hidden !important",
          }}
          spacing={4}
        >
          {/* ⭐ LEFT OUTLET CARDS WITH STAGGERED ANIMATION */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: { staggerChildren: 0.15 },
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: "1.6rem !important",
                    sm: "1.8rem !important",
                    md: "2rem !important",
                  },
                  fontWeight: "700 !important",
                  color: colors.primary.main,
                  mb: 2,
                }}
              >
                Visit & Connect
              </Typography>

              {outlets.map((outlet, idx) => {
                const countryCode = outlet?.country_code;
                const phone = outlet?.phone;

                const formattedPhone =
                  phone && countryCode
                    ? `+${countryCode} ${phone.slice(countryCode.length)}`
                    : '';
                return (
                  <motion.div
                    key={idx}
                    variants={fadeUp}
                    whileHover={{ scale: 1.03, y: -4 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card
                      elevation={3}
                      sx={{
                        mb: 2.5,
                        borderRadius: 3,
                        p: 1,
                        transition: "0.3s",
                      }}
                    >
                      <CardContent>
                        <Typography
                          sx={{
                            fontSize: "1.4rem !important",
                            fontWeight: "700 !important",
                            color: colors.primary.main,
                            mb: 1,
                          }}
                        >
                          {outlet.name}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <LocationOn sx={{ color: colors.primary.main }} />
                          <Typography
                            sx={{
                              fontSize: "1rem !important",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {outlet?.address} , {outlet?.state} - {outlet?.pincode}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                          <Phone sx={{ color: colors.primary.main }} />
                          <Typography sx={{ fontSize: "1rem !important" }}>
                            {outlet?.phone}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                          <Email sx={{ color:colors.primary.main }} />
                          <Typography sx={{ fontSize: "1rem !important" }}>
                            {outlet.email}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                          <Schedule sx={{ color: colors.primary.main }} />
                          <Typography sx={{ fontSize: "1rem !important" }}>
                            <BusinessHoursDisplay business_hours={outlet?.business_hours} />
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </motion.div>
          </Grid>

          {/* ⭐ RIGHT SIDE FORM ANIMATED */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 2.5, sm: 3.5, md: 5 },
                  borderRadius: 4,
                  position: "relative",
                  overflow: "visible !important",
                }}
              >
                <Typography
                  sx={{
                    fontSize: {
                      xs: "1.8rem !important",
                      sm: "2rem !important",
                      md: "2.1rem !important",
                    },
                    fontWeight: "700 !important",
                    color:colors.primary.main,
                    mb: 1,
                  }}
                >
                  Send Us a Message
                </Typography>

                <Typography
                  sx={{
                    fontSize: "1rem !important",
                    color: "text.secondary",
                    mb: 3,
                  }}
                >
                  Fill the form and our team will respond shortly.
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box
                  sx={{
                    overflow: "visible !important",
                  }}
                  component="form"
                  onSubmit={handleSubmit}
                >
                  <Grid
                    mt={1}
                    sx={{
                      overflow: "visible !important",
                      padding: 5,
                    }}
                    container
                    spacing={2.5}
                  >
                    {[
                      { field: "name", label: "Full Name *" },
                      {
                        field: "email",
                        label: "Email Address *",
                        type: "email",
                      },
                      { field: "phone", label: "Phone Number" },
                    ].map((item, idx) => (
                      <Grid key={idx} item xs={12} sm={6}>
                        {item.field === "phone" ? (
                          <Box sx={{
                            position: "relative",
                            // zIndex: 1300,
                            height: "56px" // Fixed height to match TextField
                          }}>

                            <PhoneInput
                              country="sg"
                              specialLabel=""
                              value={formData[item.field] || ''}
                              onChange={(phone, country) => handleNumberChange("phone", {
                                target: {
                                  value: phone,
                                  countryValue: country
                                }
                              })}
                              inputStyle={{
                                width: '100%',
                                height: '50.25px',
                                borderRadius: '7px',
                                color: 'black',
                                paddingLeft: '58px',
                                fontSize: '12px !important',
                              }}
                              buttonStyle={{
                                borderTopLeftRadius: '7px',
                                borderBottomLeftRadius: '7px',
                              }}
                              containerStyle={{
                                width: '100%',
                              }}
                            />

                          </Box>
                        ) : (
                          <TextField
                            label={item.label}
                            fullWidth
                            type={item?.type}
                            value={formData[item.field]}
                            onChange={handleInputChange(item.field)}
                          />
                        )}
                      </Grid>
                    ))}

                    {/* inquiry type */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Inquiry Type</InputLabel>
                        <Select
                          value={formData.inquiry_type}
                          label="Inquiry Type"
                          onChange={handleInputChange("inquiry_type")}
                        >
                          {inquiryTypes.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* subject */}
                    <Grid item xs={12}>
                      <TextField
                        label="Subject"
                        fullWidth
                        value={formData.subject}
                        onChange={handleInputChange("subject")}
                      />
                    </Grid>

                    {/* message */}
                    <Grid item xs={12}>
                      <TextField
                        label="Message *"
                        multiline
                        rows={6}
                        fullWidth
                        value={formData.message}
                        onChange={handleInputChange("message")}
                      />
                    </Grid>

                    {/* button */}
                    <Grid mt={1} item xs={12}>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={loading}
                          startIcon={
                            loading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <Send />
                            )
                          }
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            backgroundColor: colors.primary.main,
                            px: 4,
                            py: 1.4,
                            borderRadius: 3,
                            "&:hover": { backgroundColor:colors.secondary.main },
                          }}
                        >
                          {loading ? "Sending..." : "Send Message"}
                        </Button>
                      </motion.div>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default ContactPage;